/**
 * HUD 2.0 — combat awareness overlay (research-backed juice, 100% render-layer, zero sim/wire):
 *
 *  1. DIRECTIONAL DAMAGE ARCS — when a shot lands on YOUR hull, a red arc anchored to screen center
 *     points at the shooter (the AAA convention: rotation = bearing to attacker relative to the camera
 *     view axis, so it stays true while the camera orbits). Paired with a same-side screen-EDGE pulse —
 *     research says half of players miss either cue alone; together they read instantly.
 *  2. LOW-INTEGRITY STATE — a persistent flooding vignette that ramps from ~50% hull damage and a
 *     WebAudio heartbeat whose cadence accelerates as the ship nears sinking. The full-screen takeover
 *     is reserved for near-sink (routine hits keep only the momentary flash), per juice-tiering practice.
 *  3. COMBAT FOCUS — while in combat the menu furniture (contracts, journal, leaderboard, floating
 *     buttons) fades to ~35% via CSS `filter:opacity()` (which COMPOSES with any HUD-Forge custom
 *     opacity, unlike style.opacity), so the fight owns the screen. Panels stay tappable.
 *
 * Everything is DOM/CSS driven by per-frame tick() — no allocations in steady state, no WebGL passes.
 */

const ARC_MAX = 5;          // concurrent arcs before the oldest is recycled
const ARC_LIFE_MS = 1400;   // arc fade time
const LOW_HP = 0.35;        // heartbeat + pulse threshold (integrity 0..1)
const VIGNETTE_FROM = 0.5;  // flooding vignette starts ramping below this integrity

const CSS = `
.cfx-arc { position:fixed; left:calc(50% - 33vmin); top:calc(50% - 33vmin); width:66vmin; height:66vmin;
  pointer-events:none; z-index:38; opacity:0; will-change:transform,opacity;
  background:conic-gradient(from -28deg, transparent 0deg, rgba(255,64,48,.06) 8deg, rgba(255,64,48,.92) 28deg,
    rgba(255,64,48,.06) 48deg, transparent 56deg);
  -webkit-mask:radial-gradient(closest-side, transparent 76%, #000 81%, #000 91%, transparent 96%);
  mask:radial-gradient(closest-side, transparent 76%, #000 81%, #000 91%, transparent 96%); }
.cfx-edge { position:fixed; inset:0; pointer-events:none; z-index:37; opacity:0; will-change:opacity; }
.cfx-edge.l { background:linear-gradient(90deg, rgba(220,30,22,.5), transparent 22%); }
.cfx-edge.r { background:linear-gradient(-90deg, rgba(220,30,22,.5), transparent 22%); }
.cfx-edge.t { background:linear-gradient(180deg, rgba(220,30,22,.5), transparent 22%); }
.cfx-edge.b { background:linear-gradient(0deg, rgba(220,30,22,.5), transparent 22%); }
.cfx-lowhp { position:fixed; inset:0; pointer-events:none; z-index:39; opacity:0; transition:opacity .7s ease;
  background:radial-gradient(115% 100% at 50% 46%, transparent 52%, rgba(130,12,10,.30) 80%, rgba(95,6,5,.6) 100%); }
.cfx-lowhp.beat { animation:cfxBeat .9s ease-out; }
@keyframes cfxBeat { 0%{ filter:brightness(1) } 12%{ filter:brightness(1.55) } 30%{ filter:brightness(1.1) }
  42%{ filter:brightness(1.4) } 100%{ filter:brightness(1) } }
/* combat focus: menu furniture yields the screen while shots are flying. filter (not opacity) so it
   multiplies with a HUD-Forge custom opacity instead of clobbering it; !important-free so the Forge's
   own editing pass stays untouched (it never runs mid-combat fade thanks to the body-class reset). */
#bktoggle, #mutebtn, #invbtn, .cc-chat-toggle { transition:filter .5s ease; }
body.sor-combat #bktoggle, body.sor-combat #mutebtn, body.sor-combat #invbtn,
body.sor-combat .cc-chat-toggle { filter:opacity(.35); }
body.hudforge-on #bktoggle, body.hudforge-on #mutebtn, body.hudforge-on #invbtn,
body.hudforge-on .cc-chat-toggle { filter:none; }
`;

interface Arc { el: HTMLElement; x: number; y: number; born: number; live: boolean }

export class CombatFx {
  /** Paced by tick(); the host wires this to audio.heartbeat(). */
  onHeartbeat: (() => void) | null = null;

  private arcs: Arc[] = [];
  private edges: Record<'l' | 'r' | 't' | 'b', HTMLElement>;
  private vig: HTMLElement;
  private hp = 1;
  private lastBeat = 0;
  private combatUntil = 0;

  constructor(private bearingTo: (simX: number, simY: number) => number) {
    if (!document.getElementById('combatfx-style')) {
      const st = document.createElement('style'); st.id = 'combatfx-style'; st.textContent = CSS; document.head.appendChild(st);
    }
    const mk = (cls: string): HTMLElement => { const d = document.createElement('div'); d.className = cls; document.body.appendChild(d); return d; };
    this.edges = { l: mk('cfx-edge l'), r: mk('cfx-edge r'), t: mk('cfx-edge t'), b: mk('cfx-edge b') };
    this.vig = mk('cfx-lowhp');
    for (let i = 0; i < ARC_MAX; i++) this.arcs.push({ el: mk('cfx-arc'), x: 0, y: 0, born: 0, live: false });
  }

  /** A shot from (simX, simY) just landed on MY hull — spawn an arc pointing at the shooter. */
  hitFrom(simX: number, simY: number): void {
    let slot = this.arcs.find((a) => !a.live);
    if (!slot) { slot = this.arcs.reduce((o, a) => (a.born < o.born ? a : o)); } // recycle the oldest
    slot.x = simX; slot.y = simY; slot.born = performance.now(); slot.live = true;
    // same-side edge pulse: quantize the bearing into left/right/top/bottom of the screen
    const br = this.bearingTo(simX, simY);
    const a = Math.abs(br);
    const edge = a < Math.PI / 4 ? 't' : a > (3 * Math.PI) / 4 ? 'b' : br > 0 ? 'r' : 'l';
    const e = this.edges[edge];
    e.style.transition = 'none'; e.style.opacity = '1';
    requestAnimationFrame(() => { e.style.transition = 'opacity .55s ease-out'; e.style.opacity = '0'; });
  }

  /** Live hull integrity 0..1 (1 = dry) — drives the flooding vignette + heartbeat cadence. */
  setIntegrity(hp01: number): void { this.hp = hp01 < 0 ? 0 : hp01 > 1 ? 1 : hp01; }

  /** Rising while shots fly (either direction); menu furniture fades until it expires. */
  noteCombat(untilMs: number): void { this.combatUntil = untilMs; }

  /** Per-frame: rotate live arcs with the camera, fade them out, pace the heartbeat, toggle combat focus. */
  tick(now: number): void {
    for (const a of this.arcs) {
      if (!a.live) continue;
      const age = now - a.born;
      if (age >= ARC_LIFE_MS) { a.live = false; a.el.style.opacity = '0'; continue; }
      const deg = (this.bearingTo(a.x, a.y) * 180) / Math.PI;
      a.el.style.transform = `rotate(${deg.toFixed(1)}deg)`;
      a.el.style.opacity = String(age < 120 ? 1 : 1 - (age - 120) / (ARC_LIFE_MS - 120));
    }
    // flooding vignette: 0 at VIGNETTE_FROM integrity → full at sunk; hidden entirely above the ramp
    const ramp = this.hp >= VIGNETTE_FROM ? 0 : (VIGNETTE_FROM - this.hp) / VIGNETTE_FROM;
    this.vig.style.opacity = ramp <= 0 ? '0' : String(0.25 + ramp * 0.75);
    if (this.hp < LOW_HP) {
      const interval = this.hp < 0.15 ? 620 : 1000; // the pulse quickens as the sea wins
      if (now - this.lastBeat > interval) {
        this.lastBeat = now;
        this.onHeartbeat?.();
        this.vig.classList.remove('beat'); void this.vig.offsetWidth; this.vig.classList.add('beat');
      }
    }
    document.body.classList.toggle('sor-combat', now < this.combatUntil);
  }

  /** Hide everything instantly (stepping ashore / respawn) so no arc or vignette bleeds into a scene change. */
  reset(): void {
    for (const a of this.arcs) { a.live = false; a.el.style.opacity = '0'; }
    for (const e of Object.values(this.edges)) e.style.opacity = '0';
    this.hp = 1; this.vig.style.opacity = '0'; this.combatUntil = 0;
    document.body.classList.remove('sor-combat');
  }
}
